import { clamp, fixedFloat } from '@utils';
import { composeStruct, TextDirection } from '@strings';
import { Angle } from './Angle';
import { SERIALIZATION_PRECISION } from './GeomConstants';
import type { IPath, ParamPoint } from './IPath';
import type { PointObject } from './IPoint';
import { Point } from './Point';
import { Rect } from './Rect';
import { RectEdge } from './RectEdge';

export class LineSegment implements IPath {
  readonly #p1: Point;
  readonly #p2: Point;

  // #region Constructors

  constructor(p1: Point, p2: Point);
  constructor(x1: number, y1: number, x2: number, y2: number);
  constructor(arg1: Point | number, arg2: Point | number, arg3?: number, arg4?: number) {
    if (
      typeof arg1 === 'number' &&
      typeof arg2 === 'number' &&
      typeof arg3 === 'number' &&
      typeof arg4 === 'number'
    ) {
      this.#p1 = new Point(arg1, arg2);
      this.#p2 = new Point(arg3, arg4);
    } else if (arg1 instanceof Point && arg2 instanceof Point) {
      this.#p1 = arg1;
      this.#p2 = arg2;
    } else {
      throw new Error('Invalid arguments for LineSegment constructor');
    }

    Object.freeze(this);
  }

  recalculateCache(): void {
    // No cache
  }

  // #endregion

  // #region Attributes

  get p1(): Point {
    return this.#p1;
  }

  get p2(): Point {
    return this.#p2;
  }

  // #endregion

  // #region Comparison

  equals(other: LineSegment): boolean {
    return this === other || (this.p1.equals(other.p1) && this.p2.equals(other.p2));
  }

  isSamePath(other: IPath): boolean {
    return other instanceof LineSegment && this.equals(other);
  }

  isIsomorphicTo(other: IPath): boolean {
    return other instanceof LineSegment;
  }

  // #endregion

  // #region Type Conversion

  get array(): [number, number, number, number] {
    return [this.p1.x, this.p1.y, this.p2.x, this.p2.y];
  }

  // #endregion

  // #region Interpolation

  /**
   * Returns a line segment interpolated between this line segment and the given
   * end line segment.
   *
   * @param t The interpolation factor.
   * @param end The end line segment.
   *
   * @returns The interpolated line segment.
   */
  lerp(t: number, end: this): this {
    return new LineSegment(this.p1.lerp(t, end.p1), this.p2.lerp(t, end.p2)) as this;
  }

  /**
   * Returns a function that interpolates between this line segment and the
   * given end line segment.
   *
   * @param end The end line segment.
   *
   * @returns A function that interpolates between this line segment and the
   */
  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // #endregion

  // #region Geometric Operations

  /**
   * Returns a point interpolated between the two endpoints of the line segment.
   *
   * @param t The interpolation factor.
   *
   * @returns The interpolated point.
   */
  pointAt(t: number): Point {
    return this.p1.lerp(t, this.p2);
  }

  /**
   * Returns the length of the line segment up to `t`.
   *
   * @param t The interpolation factor.
   *
   * @returns The length of the line segment up to `t`.
   */
  lengthAt(t: number): number {
    return this.length * t;
  }

  /**
   * The derivative of the line segment at `t`.
   *
   * The derivative of a line segment at `t` is always the vector from the start
   * point to the end point.
   *
   * @param t The interpolation factor.
   *
   * @returns The derivative of the line segment at `t`.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  derivative(t: number): Point {
    return this.p2.sub(this.p1);
  }

  /**
   * The normal of the line segment at `t`.
   *
   * The normal of the line segment at `t` is the vector perpendicular to the
   * line segment, rotated 90 degrees counter-clockwise.
   *
   * @param t The interpolation factor.
   *
   * @returns The normal of the line segment at `t`.
   */
  normal(t: number): Point {
    const d = this.derivative(t);
    return new Point(-d.y, d.x).normalized();
  }

  /**
   * The angle of the line segment at `t`.
   *
   * The angle of the line segment at `t` is the angle from p1 to p2.
   *
   * @param t The interpolation factor.
   *
   * @returns The angle of the line segment at `t`.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  angle(t: number): Angle {
    return this.p1.angleTo(this.p2);
  }

  /**
   * Returns `count` number of points interpolated along the line segment.
   *
   * @param count The number of points to interpolate.
   *
   * @returns The interpolated points.
   */
  lerpPoints(count: number): Point[] {
    const points = [];
    for (let i = 0; i < count; i++) {
      points.push(this.pointAt(i / (count - 1)));
    }
    return points;
  }

  /**
   * Returns `count` number of points interpolated along the line segment, not
   * including the endpoints.
   *
   * @param count The number of points to interpolate.
   *
   * @returns The interpolated points.
   */
  divisionPoints(count: number): Point[] {
    const points = [];
    for (let i = 0; i < count; i++) {
      points.push(this.pointAt((i + 1) / (count + 1)));
    }
    return points;
  }

  /**
   * Returns the start point of the line segment.
   */
  get start(): Point {
    return this.p1;
  }

  /**
   * Returns the end point of the line segment.
   */
  get end(): Point {
    return this.p2;
  }

  /**
   * The length of the line segment.
   */
  get length(): number {
    const dx = this.p2.x - this.p1.x;
    const dy = this.p2.y - this.p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * The bounding box of the line segment.
   */
  get bounds(): Rect {
    return new Rect([this.p1, this.p2]);
  }

  /**
   * Returns a new line segment with the start and end points reversed.
   */
  reversed(): this {
    return new LineSegment(this.p2, this.p1) as this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flattened(flatness: number): ParamPoint[] {
    return [
      { t: 0, p: this.p1 },
      { t: 1, p: this.p2 }
    ];
  }

  /**
   * Adds a given point or point object to the current line segment.
   *
   * Effectively translates the line segment by the given point.
   *
   * @param other - The point or point object to add to the line segment.
   * @returns A new `LineSegment` instance with the added points.
   */
  add(other: Point | PointObject): LineSegment {
    return new LineSegment(this.p1.add(other), this.p2.add(other));
  }

  /**
   * Subtracts a given point or point object from both endpoints of the line segment.
   *
   * Effectively translates the line segment by the negated given point.
   *
   * @param other - The point or point object to subtract from the line segment.
   * @returns A new `LineSegment` instance with the subtracted points.
   */
  sub(other: Point | PointObject): LineSegment {
    return new LineSegment(this.p1.sub(other), this.p2.sub(other));
  }

  /**
   * Multiplies both endpoints of the line segment by a scalar value.
   *
   * Effectively scales the line segment by the given scalar.
   *
   * @param scalar - The scalar value to multiply the endpoints by.
   * @returns A new `LineSegment` with both endpoints scaled by the given scalar.
   */
  mul(scalar: number): LineSegment {
    return new LineSegment(this.p1.mul(scalar), this.p2.mul(scalar));
  }

  /**
   * Divides the coordinates of the line segment's endpoints by the given scalar.
   *
   * Effectively scales the line segment by the inverse of the given scalar.
   *
   * @param scalar - The scalar value by which to divide the coordinates.
   * @returns A new `LineSegment` instance with the divided coordinates.
   */
  div(scalar: number): LineSegment {
    return new LineSegment(this.p1.div(scalar), this.p2.div(scalar));
  }

  /**
   * Returns the point on the line segment closest to the given point.
   *
   * @param p The point.
   *
   * @returns An object with the following properties:
   * - p: the closest point on the line segment
   * - t: the parameter value of the closest point on the line segment
   * - d: the distance from the point to the line segment
   */
  project(p: Point): { p: Point; t: number; d: number } {
    return projectPointOntoLine(p, this.p1, this.p2);
  }

  /**
   * Determines whether this line segment intersects the given rectangle.
   *
   * @param r The rectangle.
   *
   * @returns True if the line segment intersects the rectangle, false otherwise.
   */
  intersectsRect(r: Rect): boolean {
    // First, check if either endpoint is inside the rectangle
    if (r.containsPoint(this.p1) || r.containsPoint(this.p2)) {
      return true;
    }
    // Check for intersection with any edge
    for (const edge of rectEdges(r)) {
      if (this.intersects(edge)) {
        return true;
      }
    }
    // No intersection
    return false;
  }

  /**
   * Determines whether this line segment intersects another line segment.
   *
   * @param other The other line segment.
   *
   * @returns True if the line segments intersect, false otherwise.
   */
  intersects(other: LineSegment): boolean {
    const p = this.p1;
    const r = this.p2.sub(this.p1);
    const q = other.p1;
    const s = other.p2.sub(other.p1);

    const cross_r_s = r.cross(s);
    const q_minus_p = q.sub(p);

    if (cross_r_s === 0) {
      // Lines are parallel
      const cross_qmp_r = q_minus_p.cross(r);
      if (cross_qmp_r === 0) {
        // Lines are colinear
        // Check for overlap
        const r_dot_r = r.dot(r);
        const t0 = q_minus_p.dot(r) / r_dot_r;
        const t1 = t0 + s.dot(r) / r_dot_r;
        const tmin = Math.max(0, Math.min(t0, t1));
        const tmax = Math.min(1, Math.max(t0, t1));
        return tmax >= tmin;
      } else {
        // Lines are parallel but not colinear
        return false;
      }
    } else {
      const t = q_minus_p.cross(s) / cross_r_s;
      const u = q_minus_p.cross(r) / cross_r_s;
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    }
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    return [
      fixedFloat(this.p1.x, SERIALIZATION_PRECISION),
      fixedFloat(this.p1.y, SERIALIZATION_PRECISION),
      fixedFloat(this.p2.x, SERIALIZATION_PRECISION),
      fixedFloat(this.p2.y, SERIALIZATION_PRECISION)
    ];
  }

  static fromJSON(json: unknown): LineSegment {
    if (!Array.isArray(json) || json.length !== 4) {
      throw new Error('Invalid LineSegment JSON, expected array of length 4');
    }
    if (!json.every(x => typeof x === 'number')) {
      throw new Error('Invalid LineSegment JSON, expected array of numbers');
    }
    const [x1, y1, x2, y2] = json;
    return new LineSegment(new Point(x1, y1), new Point(x2, y2));
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `LineSegment` as a string for debugging.
   */
  toString(fractionDigits?: number): string {
    return composeStruct([
      ['p1', this.p1.toString(fractionDigits)],
      ['p2', this.p2.toString(fractionDigits)]
    ]);
  }

  // #endregion
}

// #region Free Functions

/**
 * Returns the point on the line segment closest to the given point.
 *
 * @param p The point.
 * @param p1 The start point of the line segment.
 * @param p2 The end point of the line segment.
 *
 * @returns An object with the following properties:
 * - p: the closest point on the line segment
 * - t: the parameter value of the closest point on the line segment
 * - d: the distance from the point to the line segment
 */
export function projectPointOntoLine(
  p: Point,
  p1: Point,
  p2: Point
): { p: Point; t: number; d: number } {
  const x21 = p2.x - p1.x;
  const y21 = p2.y - p1.y;
  const x31 = p.x - p1.x;
  const y31 = p.y - p1.y;
  const t = clamp((x31 * x21 + y31 * y21) / (x21 * x21 + y21 * y21));
  const proj = p1.lerp(t, p2);
  return { p: proj, t: t, d: p.distanceTo(proj) };
}

export function rectEdges(r: Rect): LineSegment[] {
  return [
    new LineSegment(r.left, r.bottom, r.left, r.top), // left edge
    new LineSegment(r.left, r.top, r.right, r.top), // top edge
    new LineSegment(r.right, r.top, r.right, r.bottom), // right edge
    new LineSegment(r.right, r.bottom, r.left, r.bottom) // bottom edge
  ];
}

// Returns the line segment for the given edge.
export function rectEdge(
  r: Rect,
  edge: RectEdge,
  textDirection?: TextDirection
): LineSegment {
  if (edge === RectEdge.LEADING || edge === RectEdge.TRAILING) {
    edge = textDirection === TextDirection.LTR ? RectEdge.LEFT : RectEdge.RIGHT;
  }
  switch (edge) {
    case RectEdge.TOP:
      return new LineSegment(r.left, r.top, r.right, r.top);
    case RectEdge.RIGHT:
      return new LineSegment(r.right, r.top, r.right, r.bottom);
    case RectEdge.BOTTOM:
      return new LineSegment(r.right, r.bottom, r.left, r.bottom);
    case RectEdge.LEFT:
      return new LineSegment(r.left, r.bottom, r.left, r.top);
    default:
      throw new Error('Invalid edge');
  }
}

/**
 * Generates an array of points evenly distributed along the specified edge of
 * the rectangle.
 *
 * The array includes the starting and ending points of the edge. So if
 * `count` is 2, the array will contain the two corner points of the edge.
 *
 * @param edge - The edge of the rectangle from which to generate points.
 * @param count - The number of points to generate along the edge.
 * @returns An array of `Point` objects representing the evenly distributed
 * points along the specified edge.
 */
export function rectEdgePoints(r: Rect, edge: RectEdge, count: number): Point[] {
  return rectEdge(r, edge).lerpPoints(count);
}

/**
 * Divides the specified edge of the rectangle into a given number of points.
 *
 * The array does not include the starting and ending points of the edge. So a
 * count of 1 will return the midpoint of the edge.
 *
 * @param edge - The edge of the rectangle to be divided.
 * @param count - The number of division points to generate.
 * @returns An array of points representing the division points along the specified edge.
 */
export function rectEdgeDivisionPoints(r: Rect, edge: RectEdge, count: number): Point[] {
  return rectEdge(r, edge).divisionPoints(count);
}

// #endregion
